import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Paper, Stack, Text, Title } from '@mantine/core';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { format, parseISO } from 'date-fns';
import { VEHICLE_TYPE_LABEL } from '#/components/ui/tickets/compulsory-insurance-penalty/model';
import { TICKET_TYPE_CONFIG } from '#/components/ui/tickets/config.ts';
import { PAY_INTERVAL_LABEL } from '#/components/ui/tickets/enrollments/model.ts';
import { getLoadErrorMessage } from '#/service/driver-management/utils/apiError.ts';
import {
  useAccountEnrollments,
  type AccountEnrollmentRow,
} from '../hooks/useAccountEnrollments.ts';
import { SectionState } from './SectionState.tsx';

const TITLE_STYLE = { color: 'gray', fontWeight: 'normal' };

const RECORDS_PER_PAGE = 5;

const columns: DataTableColumn<AccountEnrollmentRow>[] = [
  { title: '車牌號碼', titleStyle: TITLE_STYLE, accessor: 'plateNumber' },
  {
    title: '車種',
    titleStyle: TITLE_STYLE,
    accessor: 'vehicleType',
    render: ({ vehicleType }) =>
      vehicleType ? VEHICLE_TYPE_LABEL[vehicleType] : '-',
  },
  {
    title: '證件號碼／統編',
    titleStyle: TITLE_STYLE,
    accessor: 'idNumber',
    render: ({ idNumber, taxIdNumber }) => idNumber ?? taxIdNumber ?? '-',
  },
  {
    title: '申請服務',
    titleStyle: TITLE_STYLE,
    accessor: 'type',
    render: ({ type }) => TICKET_TYPE_CONFIG[type].name,
  },
  {
    title: '查繳週期',
    titleStyle: TITLE_STYLE,
    accessor: 'payInterval',
    render: ({ payInterval }) => (
      <Badge variant="light" size="lg">
        {PAY_INTERVAL_LABEL[payInterval]}
      </Badge>
    ),
  },
  {
    title: '狀態',
    titleStyle: TITLE_STYLE,
    accessor: 'state',
    render: ({ state }) =>
      state === 'ACTIVE' ? (
        <Badge variant="light" size="lg" color="green">
          使用中
        </Badge>
      ) : (
        <Badge variant="light" size="lg" color="red">
          已停用
        </Badge>
      ),
  },
  {
    title: '申請時間',
    titleStyle: TITLE_STYLE,
    accessor: 'createDateTime',
    render: ({ createDateTime }) =>
      format(parseISO(createDateTime), 'yyyy/MM/dd HH:mm:ss'),
  },
  {
    title: '操作',
    titleStyle: TITLE_STYLE,
    accessor: 'id',
    render: ({ plateNumber, type }) => (
      <Button
        size="xs"
        variant="outline"
        component={Link}
        to={`/tickets/enrollments?${new URLSearchParams({ q: plateNumber, type })}`}
      >
        查看申請單
      </Button>
    ),
  },
];

const AutoPaySection = ({ accountId }: { accountId: string }) => {
  const { isPending, rows, errors } = useAccountEnrollments(accountId);
  const [page, setPage] = useState(1);

  return (
    <Paper radius="lg" p="xl" shadow="xs">
      <Title order={4} mb="md">
        通行費自動繳費狀態
      </Title>
      <Stack gap="md">
        {errors.length > 0 && (
          <Alert color="red" variant="light">
            {errors.map(({ type, error }) => (
              <Text key={type} size="sm">
                {TICKET_TYPE_CONFIG[type].name}：{getLoadErrorMessage(error)}
              </Text>
            ))}
          </Alert>
        )}
        <DataTable
          idAccessor="id"
          verticalSpacing="sm"
          columns={columns}
          records={rows.slice(
            (page - 1) * RECORDS_PER_PAGE,
            page * RECORDS_PER_PAGE,
          )}
          fetching={isPending}
          minHeight={200}
          totalRecords={rows.length}
          recordsPerPage={RECORDS_PER_PAGE}
          page={page}
          onPageChange={setPage}
          paginationText={({ from, to, totalRecords }) =>
            `顯示 ${from} - ${to} 筆，共 ${totalRecords} 筆`
          }
          styles={{ pagination: { color: 'gray' } }}
          emptyState={
            <SectionState
              status="empty"
              message={
                errors.length > 0
                  ? '沒有可顯示的資料，請見上方錯誤訊息'
                  : '沒有通行費自動繳費資料'
              }
            />
          }
          customLoader={<SectionState status="loading" />}
        />
      </Stack>
    </Paper>
  );
};

export { AutoPaySection };
